import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Input, Select, DataTable, Badge, Tabs, Toast } from '../ui';
import { useApp } from '../../contexts/AppContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getTransactionsByOwner } from '../../services/transactionService';
import { getReceiptsByOrganization, calculateUserOutstandingBalance, getReceiptFinancialStatusCounts } from '../../services/receiptService';
import { getSettingsByOrganization } from '../../services/settingsService';
import { getUserFinancialStatuses } from '../../services/userService';
import { Player, User, Transaction, Receipt, FinancialStatus } from '../../types';
import { searchPlayers, searchUsers } from '../../services/algoliaService';
import { getPlayersByGuardianId } from '../../services/playerService';
import { getUserById } from '../../services/userService';

interface PlayerFinancialSummary {
  player: Player;
  user: User;
  guardians: User[];
  outstandingDebits: number;
  availableCredits: number;
  netBalance: number;
  recentTransactions: Transaction[];
  receipts: Receipt[];
  totalTransactionAmount: number;
}

interface GuardianFinancialSummary {
  guardian: User;
  linkedPlayers: PlayerFinancialSummary[];
  totalOutstanding: number;
  totalCredits: number;
  totalNetBalance: number;
  allTransactions: Transaction[];
  allReceipts: Receipt[];
  totalTransactionAmount: number;
}

const PlayersGuardians: React.FC = () => {
  const { selectedOrganization, selectedAcademy } = useApp();
  const { canWrite } = usePermissions();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [defaultCurrency, setDefaultCurrency] = useState<string>('USD');

  // Pagination state for players
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const playersPerPage = 10;

  // Default sort (name asc) uses Algolia server-side pagination; all other sorts fetch all and paginate locally
  const isAlgoliaSorted = sortBy === 'name' && sortDirection === 'asc';

  // Algolia pagination state for guardians
  const [guardianCurrentPage, setGuardianCurrentPage] = useState(0);
  const [guardianTotalPages, setGuardianTotalPages] = useState(0);
  const [totalGuardians, setTotalGuardians] = useState(0);
  const [guardianLoading, setGuardianLoading] = useState(false);

  // Data state
  const [playerFinancials, setPlayerFinancials] = useState<PlayerFinancialSummary[]>([]);
  const [guardianFinancials, setGuardianFinancials] = useState<GuardianFinancialSummary[]>([]);
  const [selectedGuardian, setSelectedGuardian] = useState<GuardianFinancialSummary | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Track which players have had detailed financial data loaded (avoids re-fetching on page change)
  const loadedPlayerIdsRef = useRef<Set<string>>(new Set());
  // Track which players are currently loading financial data
  const [loadingFinancialIds, setLoadingFinancialIds] = useState<Set<string>>(new Set());
  // Ref to track current visible players after sort/filter (used by page-change effect)
  const visiblePlayersRef = useRef<PlayerFinancialSummary[]>([]);

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Financial status map from user documents
  const [financialStatusMap, setFinancialStatusMap] = useState<Map<string, FinancialStatus>>(new Map());

  // Organization-wide financial stats (receipt-based counts)
  const [orgStats, setOrgStats] = useState<{ settledCount: number; pendingCount: number; overdueCount: number } | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(0); // Reset to first page on new search
      setGuardianCurrentPage(0); // Reset guardian page too
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Algolia page only matters when sorting by name (server-side sort + pagination)
  // For financial sorting, all players are fetched at once and paginated locally
  const algoliaPage = isAlgoliaSorted ? currentPage : 0;

  // Re-fetch when Algolia query parameters change
  useEffect(() => {
    if (selectedOrganization?.id) {
      loadFinancialData();
    }
  }, [selectedOrganization, selectedAcademy, debouncedSearchTerm, sortBy, sortDirection, algoliaPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // When page changes in local-sort mode, load detailed data for the new visible players
  useEffect(() => {
    if (!isAlgoliaSorted && visiblePlayersRef.current.length > 0 && selectedOrganization?.id) {
      loadDetailedFinancialData(visiblePlayersRef.current).catch(error => {
        console.error('Background financial data loading failed for page:', error);
      });
    }
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load guardians data when guardians tab is active or pagination changes
  useEffect(() => {
    if (selectedOrganization?.id && activeTab === 1) {
      loadGuardiansData();
    }
  }, [selectedOrganization, selectedAcademy, debouncedSearchTerm, guardianCurrentPage, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh data when the page becomes visible (user returns from transaction page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && selectedOrganization?.id) {
        console.log('📊 Page became visible, refreshing financial data...');
        loadFinancialData();
      }
    };

    const handleFocus = () => {
      if (selectedOrganization?.id) {
        console.log('📊 Window focused, refreshing financial data...');
        loadFinancialData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [selectedOrganization?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFinancialData = async () => {
    if (!selectedOrganization?.id) return;

    // Clear loaded player cache when re-fetching from Algolia (new search/sort/org change)
    loadedPlayerIdsRef.current.clear();

    try {
      setLoading(true);

      // Load currency from settings
      const settingsData = await getSettingsByOrganization(selectedOrganization.id);
      if (settingsData?.generalSettings?.currency) {
        setDefaultCurrency(settingsData.generalSettings.currency);
      }

      // Use Algolia PLAYERS index directly (much more efficient)
      console.log('🔎 Searching players via Algolia players index...');
      console.log('📌 Organization ID:', selectedOrganization.id);
      console.log('📌 Academy ID filter:', selectedAcademy?.id || 'none');

      let algoliaResults;
      try {
        // Default sort (name asc): Algolia handles pagination (fetch one page)
        // All other sorts: fetch all players, sort and paginate locally
        algoliaResults = await searchPlayers({
          query: debouncedSearchTerm,
          organizationId: selectedOrganization.id,
          filters: {
            academyId: selectedAcademy?.id
          },
          page: isAlgoliaSorted ? currentPage : 0,
          hitsPerPage: isAlgoliaSorted ? playersPerPage : 1000
        });
      } catch (algoliaError) {
        console.error('❌ Algolia search failed:', algoliaError);
        showToast('Search failed. The players index may not be configured. Run syncPlayersToAlgolia() in console.', 'error');
        setPlayerFinancials([]);
        setGuardianFinancials([]);
        setLoading(false);
        return;
      }

      console.log(`✅ Algolia returned ${algoliaResults.players.length} players (page ${algoliaResults.currentPage + 1}/${algoliaResults.totalPages})`);
      console.log('📊 Total players in index:', algoliaResults.totalPlayers);

      // Update pagination state
      setTotalPages(algoliaResults.totalPages);
      setTotalPlayers(algoliaResults.totalPlayers);

      // If no results, show helpful message
      if (algoliaResults.players.length === 0) {
        console.warn('⚠️ No players found in Algolia. You may need to run syncPlayersToAlgolia() in the browser console.');
        if (algoliaResults.totalPlayers === 0 && !debouncedSearchTerm) {
          showToast('No players in index. Open browser console and run: syncPlayersToAlgolia()', 'info');
        }
        setPlayerFinancials([]);
        setGuardianFinancials([]);
        setLoading(false);
        return;
      }

      // Build player summaries directly from Algolia player data (no Firebase calls needed)
      const playerSummaries: PlayerFinancialSummary[] = [];

      for (const algoliaPlayer of algoliaResults.players) {
        // Convert Algolia player to Player type
        const player: Player = {
          id: algoliaPlayer.objectID,
          userId: algoliaPlayer.userId,
          organizationId: algoliaPlayer.organizationId,
          academyId: algoliaPlayer.academyId || [],
          guardianId: algoliaPlayer.guardianId || [],
          dob: new Date(),
          gender: '',
          playerParameters: {},
          status: algoliaPlayer.status,
          createdAt: algoliaPlayer.createdAt ? {
            toDate: () => new Date(algoliaPlayer.createdAt!),
            seconds: Math.floor((algoliaPlayer.createdAt || 0) / 1000),
            nanoseconds: 0,
            toMillis: () => algoliaPlayer.createdAt || 0,
            isEqual: () => false,
            toJSON: () => ({ seconds: Math.floor((algoliaPlayer.createdAt || 0) / 1000), nanoseconds: 0 })
          } as any : { toDate: () => new Date() } as any,
          updatedAt: algoliaPlayer.updatedAt ? {
            toDate: () => new Date(algoliaPlayer.updatedAt!),
            seconds: Math.floor((algoliaPlayer.updatedAt || 0) / 1000),
            nanoseconds: 0,
            toMillis: () => algoliaPlayer.updatedAt || 0,
            isEqual: () => false,
            toJSON: () => ({ seconds: Math.floor((algoliaPlayer.updatedAt || 0) / 1000), nanoseconds: 0 })
          } as any : { toDate: () => new Date() } as any
        };

        // Convert Algolia player user data to User type
        const user: User = {
          id: algoliaPlayer.userId,
          name: algoliaPlayer.userName || '',
          email: algoliaPlayer.userEmail || '',
          phone: algoliaPlayer.userPhone,
          photoURL: algoliaPlayer.userPhotoURL,
          roles: [],
          createdAt: player.createdAt,
          updatedAt: player.updatedAt
        };

        // Build guardians from Algolia data (guardianId + guardianNames)
        // If guardianNames is missing from Algolia, fetch the actual name from Firestore
        const guardians: User[] = [];
        if (algoliaPlayer.guardianId && algoliaPlayer.guardianId.length > 0) {
          for (let index = 0; index < algoliaPlayer.guardianId.length; index++) {
            const guardianId = algoliaPlayer.guardianId[index];
            let guardianName = algoliaPlayer.guardianNames?.[index];

            // Fetch real name from Firestore if Algolia doesn't have it
            if (!guardianName || guardianName === 'Guardian') {
              try {
                const guardianUser = await getUserById(guardianId);
                guardianName = guardianUser?.name || 'Guardian';
              } catch {
                guardianName = 'Guardian';
              }
            }

            guardians.push({
              id: guardianId,
              name: guardianName,
              email: '',
              roles: [],
              createdAt: player.createdAt,
              updatedAt: player.updatedAt
            });
          }
        }

        // For initial load, just show basic info with placeholders
        playerSummaries.push({
          player,
          user,
          guardians,
          outstandingDebits: 0,
          availableCredits: 0,
          netBalance: 0,
          recentTransactions: [],
          receipts: [],
          totalTransactionAmount: 0
        });
      }

      // Batch-fetch financial statuses for all player userIds on this page
      const playerUserIds = playerSummaries.map(ps => ps.player.userId).filter(Boolean);
      if (playerUserIds.length > 0) {
        try {
          const statusMap = await getUserFinancialStatuses(playerUserIds, selectedOrganization.id);
          setFinancialStatusMap(statusMap);
        } catch (err) {
          console.error('Error fetching financial statuses:', err);
        }
      }

      setPlayerFinancials(playerSummaries);

      // Mark all players as loading their financial data
      setLoadingFinancialIds(new Set(playerSummaries.map(ps => ps.player.userId)));

      // Generate basic guardian summaries
      const guardianMap = new Map<string, GuardianFinancialSummary>();
      const processedPlayersByGuardian = new Map<string, Set<string>>();

      playerSummaries.forEach(playerSummary => {
        playerSummary.guardians.forEach(guardian => {
          if (!guardianMap.has(guardian.id)) {
            guardianMap.set(guardian.id, {
              guardian,
              linkedPlayers: [],
              totalOutstanding: 0,
              totalCredits: 0,
              totalNetBalance: 0,
              allTransactions: [],
              allReceipts: [],
              totalTransactionAmount: 0
            });
            processedPlayersByGuardian.set(guardian.id, new Set());
          }

          const guardianSummary = guardianMap.get(guardian.id)!;
          const processedPlayers = processedPlayersByGuardian.get(guardian.id)!;

          // Prevent double-counting the same player
          if (!processedPlayers.has(playerSummary.player.userId)) {
            processedPlayers.add(playerSummary.player.userId);
            guardianSummary.linkedPlayers.push(playerSummary);
          }
        });
      });

      // Check if any guardian is also a player and add them to their own linked players list
      guardianMap.forEach((guardianSummary, guardianId) => {
        const processedPlayers = processedPlayersByGuardian.get(guardianId) || new Set();

        // Find if this guardian is also a player - check multiple possible matches
        const guardianAsPlayer = playerSummaries.find(
          ps => ps.player.userId === guardianId || ps.user.id === guardianId
        );

        if (guardianAsPlayer) {
          const alreadyIncluded = processedPlayers.has(guardianAsPlayer.player.userId) ||
            guardianSummary.linkedPlayers.some(
              lp => lp.player.userId === guardianAsPlayer.player.userId || lp.player.id === guardianAsPlayer.player.id
            );

          if (!alreadyIncluded) {
            // Add to linked players with a flag to identify it's their own account
            guardianSummary.linkedPlayers.unshift({
              ...guardianAsPlayer,
              user: {
                ...guardianAsPlayer.user,
                name: `${guardianAsPlayer.user.name} (Own Account)`
              }
            });
            processedPlayers.add(guardianAsPlayer.player.userId);
          }
        }
      });

      setGuardianFinancials(Array.from(guardianMap.values()));

      // Load detailed financial data in background — only for the visible page
      const visiblePage = isAlgoliaSorted
        ? playerSummaries // Already just one page from Algolia
        : playerSummaries.slice(0, playersPerPage); // First page for local sort
      setTimeout(() => {
        loadDetailedFinancialData(visiblePage).catch(error => {
          console.error('Background financial data loading failed:', error);
          showToast('Some financial data may be incomplete', 'info');
        });
      }, 100);

    } catch (error) {
      console.error('Error loading financial data:', error);
      showToast('Failed to load financial data', 'error');
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  };

  // Load guardians data separately with pagination
  const loadGuardiansData = async () => {
    if (!selectedOrganization?.id) return;

    try {
      setGuardianLoading(true);
      console.log('🔎 Searching guardians via Algolia users index...');

      // Search users with guardian role
      const algoliaResults = await searchUsers({
        query: debouncedSearchTerm,
        organizationId: selectedOrganization.id,
        filters: {
          role: 'guardian',
          academyId: selectedAcademy?.id
        },
        page: guardianCurrentPage,
        hitsPerPage: 10
      });

      console.log(`✅ Algolia returned ${algoliaResults.users.length} guardians (page ${algoliaResults.currentPage + 1}/${algoliaResults.totalPages})`);

      // Update pagination state
      setGuardianTotalPages(algoliaResults.totalPages);
      setTotalGuardians(algoliaResults.totalUsers);

      // Build guardian summaries
      const guardianSummaries: GuardianFinancialSummary[] = [];

      for (const algoliaGuardian of algoliaResults.users) {
        // Get linked players for this guardian
        const linkedPlayers = await getPlayersByGuardianId(algoliaGuardian.objectID);

        // Build player financial summaries for linked players
        const linkedPlayerSummaries: PlayerFinancialSummary[] = [];

        for (const player of linkedPlayers) {
          // Get user data for the player
          const playerUser = await getUserById(player.userId);
          if (!playerUser) continue;

          // Get financial data
          let balanceInfo = {
            outstandingDebits: 0,
            availableCredits: 0,
            netBalance: 0
          };

          try {
            balanceInfo = await calculateUserOutstandingBalance(player.userId, selectedOrganization.id);
          } catch (error) {
            console.error(`Error calculating balance for player ${player.userId}:`, error);
          }

          // Calculate net balance: Negative = owes money, Positive = has credit
          const netBalance = balanceInfo.availableCredits - balanceInfo.outstandingDebits;

          linkedPlayerSummaries.push({
            player,
            user: playerUser,
            guardians: [],
            outstandingDebits: balanceInfo.outstandingDebits,
            availableCredits: balanceInfo.availableCredits,
            netBalance,
            recentTransactions: [],
            receipts: [],
            totalTransactionAmount: 0
          });
        }

        // Calculate totals for this guardian
        const totalOutstanding = linkedPlayerSummaries.reduce((sum, p) => sum + p.outstandingDebits, 0);
        const totalCredits = linkedPlayerSummaries.reduce((sum, p) => sum + p.availableCredits, 0);
        const totalNetBalance = linkedPlayerSummaries.reduce((sum, p) => sum + p.netBalance, 0);

        guardianSummaries.push({
          guardian: {
            id: algoliaGuardian.objectID,
            name: algoliaGuardian.name || 'Guardian',
            email: algoliaGuardian.email || '',
            phone: algoliaGuardian.phone,
            photoURL: algoliaGuardian.photoURL,
            roles: [],
            createdAt: new Date() as any,
            updatedAt: new Date() as any
          },
          linkedPlayers: linkedPlayerSummaries,
          totalOutstanding,
          totalCredits,
          totalNetBalance,
          allTransactions: [],
          allReceipts: [],
          totalTransactionAmount: 0
        });
      }

      setGuardianFinancials(guardianSummaries);

    } catch (error) {
      console.error('Error loading guardians data:', error);
      showToast('Failed to load guardians data', 'error');
    } finally {
      setGuardianLoading(false);
    }
  };

  const loadDetailedFinancialData = async (visiblePlayers: PlayerFinancialSummary[]) => {
    if (!selectedOrganization?.id) {
      console.warn('No organization selected for financial data loading');
      return;
    }

    // Only process players that haven't been loaded yet
    const playersToLoad = visiblePlayers.filter(
      ps => !loadedPlayerIdsRef.current.has(ps.player.userId)
    );

    if (playersToLoad.length === 0) {
      console.log('✅ All visible players already have detailed data loaded');
      return;
    }

    console.log(`📊 Loading detailed financial data for ${playersToLoad.length} of ${visiblePlayers.length} visible players`);

    // Mark players as loading
    const loadingIds = new Set(playersToLoad.map(ps => ps.player.userId));
    setLoadingFinancialIds(prev => new Set([...prev, ...loadingIds]));

    try {

      // Load all organization receipts once for efficiency
      let allOrgReceipts: Receipt[] = [];
      try {
        allOrgReceipts = await getReceiptsByOrganization(selectedOrganization.id);
        console.log(`✅ Loaded ${allOrgReceipts.length} total receipts for organization`);
      } catch (receiptError) {
        console.error('Error loading organization receipts:', receiptError);
      }

      // Load financial data only for players not yet loaded
      const updatedPlayerMap = new Map<string, PlayerFinancialSummary>();

      await Promise.all(
        playersToLoad.map(async (playerSummary) => {
          try {
            // Get transactions
            let transactions: Transaction[] = [];
            try {
              const userRef = { id: playerSummary.player.userId } as any;
              transactions = await getTransactionsByOwner(selectedOrganization.id, userRef);
            } catch (transactionError) {
              console.error(`Error loading transactions for ${playerSummary.user.name}:`, transactionError);
            }

            let balanceInfo = {
              outstandingDebits: 0,
              availableCredits: 0,
              netBalance: 0,
              pendingDebitReceipts: [] as Receipt[],
              creditReceipts: [] as Receipt[]
            };

            try {
              balanceInfo = await calculateUserOutstandingBalance(
                playerSummary.player.userId,
                selectedOrganization.id
              );
            } catch (balanceError) {
              console.error(`Error calculating balance for ${playerSummary.user.name}:`, balanceError);
            }

            const organizationReceipts = allOrgReceipts.filter(r => {
              return r.userRef && r.userRef.id === playerSummary.player.userId &&
                     r.organizationId === selectedOrganization.id &&
                     r.status !== 'deleted' &&
                     (r.status === 'active' || r.status === 'paid' || r.status === 'completed' || !r.status);
            });

            const netBalance = balanceInfo.availableCredits - balanceInfo.outstandingDebits;
            const totalAmount = transactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);

            updatedPlayerMap.set(playerSummary.player.userId, {
              ...playerSummary,
              outstandingDebits: balanceInfo.outstandingDebits,
              availableCredits: balanceInfo.availableCredits,
              netBalance,
              recentTransactions: transactions.slice(0, 5),
              receipts: organizationReceipts,
              totalTransactionAmount: totalAmount
            });

            // Mark as loaded
            loadedPlayerIdsRef.current.add(playerSummary.player.userId);

          } catch (playerError) {
            console.error(`Error processing player ${playerSummary.user.name}:`, playerError);
            updatedPlayerMap.set(playerSummary.player.userId, {
              ...playerSummary,
              outstandingDebits: 0,
              availableCredits: 0,
              netBalance: 0,
              recentTransactions: [],
              receipts: [],
              totalTransactionAmount: 0
            });
            loadedPlayerIdsRef.current.add(playerSummary.player.userId);
          }
        })
      );

      // Merge updated players back into the full array
      setPlayerFinancials(prev =>
        prev.map(ps => updatedPlayerMap.get(ps.player.userId) || ps)
      );

      // Update guardian summaries using ALL current player data (including newly loaded)
      setPlayerFinancials(latestPlayers => {
        const guardianMap = new Map<string, GuardianFinancialSummary>();
        const processedPlayersByGuardian = new Map<string, Set<string>>();

        latestPlayers.forEach(playerSummary => {
          playerSummary.guardians.forEach(guardian => {
            if (!guardianMap.has(guardian.id)) {
              guardianMap.set(guardian.id, {
                guardian,
                linkedPlayers: [],
                totalOutstanding: 0,
                totalCredits: 0,
                totalNetBalance: 0,
                allTransactions: [],
                allReceipts: [],
                totalTransactionAmount: 0
              });
              processedPlayersByGuardian.set(guardian.id, new Set());
            }

            const guardianSummary = guardianMap.get(guardian.id)!;
            const processedPlayers = processedPlayersByGuardian.get(guardian.id)!;

            if (!processedPlayers.has(playerSummary.player.userId)) {
              processedPlayers.add(playerSummary.player.userId);
              guardianSummary.linkedPlayers.push(playerSummary);
              guardianSummary.totalOutstanding += playerSummary.outstandingDebits;
              guardianSummary.totalCredits += playerSummary.availableCredits;
              guardianSummary.totalNetBalance += playerSummary.netBalance;
              guardianSummary.totalTransactionAmount += playerSummary.totalTransactionAmount;
              guardianSummary.allTransactions.push(...playerSummary.recentTransactions);
              guardianSummary.allReceipts.push(...playerSummary.receipts);
            }
          });
        });

        // Check if any guardian is also a player
        guardianMap.forEach((guardianSummary, guardianId) => {
          const processedPlayers = processedPlayersByGuardian.get(guardianId) || new Set();
          const guardianAsPlayer = latestPlayers.find(
            ps => ps.player.userId === guardianId || ps.user.id === guardianId
          );

          if (guardianAsPlayer) {
            const alreadyIncluded = processedPlayers.has(guardianAsPlayer.player.userId) ||
              guardianSummary.linkedPlayers.some(
                lp => lp.player.userId === guardianAsPlayer.player.userId || lp.player.id === guardianAsPlayer.player.id
              );

            if (!alreadyIncluded) {
              guardianSummary.totalOutstanding += guardianAsPlayer.outstandingDebits;
              guardianSummary.totalCredits += guardianAsPlayer.availableCredits;
              guardianSummary.totalNetBalance += guardianAsPlayer.netBalance;
              guardianSummary.totalTransactionAmount += guardianAsPlayer.totalTransactionAmount;
              guardianSummary.allTransactions.push(...guardianAsPlayer.recentTransactions);
              guardianSummary.allReceipts.push(...guardianAsPlayer.receipts);

              guardianSummary.linkedPlayers.unshift({
                ...guardianAsPlayer,
                user: {
                  ...guardianAsPlayer.user,
                  name: `${guardianAsPlayer.user.name} (Own Account)`
                }
              });
              processedPlayers.add(guardianAsPlayer.player.userId);
            }
          }
        });

        setGuardianFinancials(Array.from(guardianMap.values()));
        return latestPlayers; // No change to players, just needed latest state for guardian calc
      });

      // Clear loading state for these players
      setLoadingFinancialIds(prev => {
        const next = new Set(prev);
        loadingIds.forEach(id => next.delete(id));
        return next;
      });

    } catch (error) {
      console.error('Error loading detailed financial data:', error);
      showToast(`Failed to load financial data: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
      // Clear loading state on error too
      setLoadingFinancialIds(prev => {
        const next = new Set(prev);
        loadingIds.forEach(id => next.delete(id));
        return next;
      });
    }
  };

  // Load organization-wide financial stats from receipt financialStatus fields
  const loadOrganizationStats = async () => {
    if (!selectedOrganization?.id) return;

    try {
      setLoadingStats(true);
      const stats = await getReceiptFinancialStatusCounts(
        selectedOrganization.id,
        selectedAcademy?.id
      );
      setOrgStats(stats);
      console.log('📊 Organization stats loaded:', stats);
    } catch (error) {
      console.error('Error loading organization stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Load organization stats when organization or academy changes
  useEffect(() => {
    if (selectedOrganization?.id) {
      loadOrganizationStats();
    }
  }, [selectedOrganization?.id, selectedAcademy?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
  };



  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Settled' },
    { value: 'pending', label: 'Pending Payment' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'credit', label: 'Has Credits' }
  ];

  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'balance', label: 'Net Balance' },
    { value: 'outstanding', label: 'Outstanding' },
    { value: 'credits', label: 'Credits' }
  ];

  const getFinancialStatus = (playerSummary: PlayerFinancialSummary): string => {
    // Check if player has any credit (positive balance)
    if (playerSummary.netBalance > 0) return 'credit';

    // If detailed financial data has been loaded, derive status from the actual balance
    if (loadedPlayerIdsRef.current.has(playerSummary.player.userId)) {
      if (playerSummary.netBalance === 0) return 'active'; // settled
      // netBalance < 0 means owes money — check user document for overdue vs pending
      const userId = playerSummary.player.userId;
      const status = financialStatusMap.get(userId) || 'overdue';
      if (status === 'overdue') return 'overdue';
      if (status === 'pending') return 'pending';
      return 'overdue'; // default to overdue if balance is negative
    }

    // Fallback to pre-fetched map (before detailed data loads)
    const userId = playerSummary.player.userId;
    const status = financialStatusMap.get(userId) || 'settled';
    if (status === 'overdue') return 'overdue';
    if (status === 'pending') return 'pending';
    return 'active'; // settled
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'credit':
        return 'primary';
      default:
        return 'secondary';
    }
  };

  const getStatusDisplayLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'SETTLED';
      case 'pending':
        return 'PENDING';
      case 'overdue':
        return 'OVERDUE';
      case 'credit':
        return 'CREDIT';
      default:
        return status.toUpperCase();
    }
  };

  const handleSort = (key: string) => {
    if (sortBy === key) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortBy(key);
      setSortDirection('asc');
    }
    setCurrentPage(0); // Reset to first page when sort changes
  };

  // Filter players by status (always local since financial status is computed locally)
  const statusFilteredPlayers = playerFinancials.filter(playerSummary => {
    const status = getFinancialStatus(playerSummary);
    return filterStatus === 'all' || status === filterStatus;
  });

  // Name sorting: Algolia already sorted + paginated, just apply status filter
  // Financial sorting: sort all players locally, then paginate
  let filteredPlayers: PlayerFinancialSummary[];
  let effectiveTotalPages: number;
  let effectiveTotalCount: number;

  if (isAlgoliaSorted) {
    // Algolia handles sort + pagination; we only filter by status locally
    filteredPlayers = statusFilteredPlayers;
    effectiveTotalPages = totalPages;
    effectiveTotalCount = totalPlayers;
  } else {
    // Sort all players locally
    const sorted = [...statusFilteredPlayers].sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      switch (sortBy) {
        case 'name':
          return a.user.name.localeCompare(b.user.name) * direction;
        case 'balance':
          return (a.netBalance - b.netBalance) * direction;
        case 'outstanding':
          return (a.outstandingDebits - b.outstandingDebits) * direction;
        case 'credits':
          return (a.availableCredits - b.availableCredits) * direction;
        case 'status': {
          const statusOrder: Record<string, number> = { 'overdue': 0, 'pending': 1, 'active': 2, 'credit': 3 };
          const statusA = getFinancialStatus(a);
          const statusB = getFinancialStatus(b);
          return ((statusOrder[statusA] || 0) - (statusOrder[statusB] || 0)) * direction;
        }
        default:
          return 0;
      }
    });

    // Paginate locally
    effectiveTotalCount = sorted.length;
    effectiveTotalPages = Math.ceil(sorted.length / playersPerPage);
    filteredPlayers = sorted.slice(
      currentPage * playersPerPage,
      (currentPage + 1) * playersPerPage
    );
  }

  // Keep ref in sync so the page-change effect can load data for the right players
  visiblePlayersRef.current = filteredPlayers;

  // Guardians filtering - search is done locally since guardians come from player summaries
  const filteredGuardians = guardianFinancials
    .filter(guardianSummary => {
      const matchesSearch =
        guardianSummary.guardian.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        guardianSummary.guardian.email.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        guardianSummary.linkedPlayers.some(p => p.user.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()));

      const hasStatus = guardianSummary.linkedPlayers.some(p => {
        const status = getFinancialStatus(p);
        return filterStatus === 'all' || status === filterStatus;
      });

      return matchesSearch && hasStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'balance':
          return b.totalNetBalance - a.totalNetBalance;
        case 'outstanding':
          return b.totalOutstanding - a.totalOutstanding;
        case 'credits':
          return b.totalCredits - a.totalCredits;
        default:
          return a.guardian.name.localeCompare(b.guardian.name);
      }
    });

  const playerColumns = [
    {
      key: 'player',
      header: 'Player',
      sortable: true,
      sortKey: 'name',
      render: (playerSummary: PlayerFinancialSummary) => (
        <div>
          <div className="font-medium text-secondary-900">{playerSummary.user.name}</div>
          <div className="text-sm text-secondary-600">{playerSummary.user.email}</div>
          {playerSummary.player.createdAt && (
            <div className="text-xs text-secondary-500">
              Joined: {playerSummary.player.createdAt.toDate().toLocaleDateString()}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'guardian',
      header: 'Guardian(s)',
      render: (playerSummary: PlayerFinancialSummary) => (
        <div>
          {playerSummary.guardians.length > 0 ? (
            playerSummary.guardians.map((guardian, index) => (
              <div key={guardian.id} className={`${index > 0 ? 'mt-2 pt-2 border-t' : ''}`}>
                <div className="font-medium text-secondary-900">
                  {guardian.name}
                </div>
                <div className="text-sm text-secondary-600">{guardian.email}</div>
                {guardian.phone && (
                  <div className="text-xs text-secondary-500">{guardian.phone}</div>
                )}
              </div>
            ))
          ) : (
            <span className="text-secondary-500">No guardian assigned</span>
          )}
        </div>
      )
    },
    {
      key: 'financialSummary',
      header: 'Financial Summary',
      sortable: true,
      sortKey: 'balance',
      render: (playerSummary: PlayerFinancialSummary) => {
        if (loadingFinancialIds.has(playerSummary.player.userId)) {
          return (
            <div className="flex items-center gap-2 text-secondary-500">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600"></div>
              <span className="text-sm">Calculating...</span>
            </div>
          );
        }
        return (
          <div className={`font-medium ${playerSummary.netBalance > 0 ? 'text-success-600' : playerSummary.netBalance < 0 ? 'text-error-600' : 'text-secondary-900'}`}>
            Net Balance: {defaultCurrency} {playerSummary.netBalance.toFixed(2)}
          </div>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortKey: 'status',
      render: (playerSummary: PlayerFinancialSummary) => {
        if (loadingFinancialIds.has(playerSummary.player.userId)) {
          return (
            <div className="flex items-center gap-2 text-secondary-500">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600"></div>
            </div>
          );
        }
        const status = getFinancialStatus(playerSummary);
        return (
          <Badge variant={getStatusColor(status)}>
            {getStatusDisplayLabel(status)}
          </Badge>
        );
      }
    },
    { 
      key: 'actions', 
      header: 'Actions',
      render: (playerSummary: PlayerFinancialSummary) => (
        <div className="flex items-center space-x-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate(`/finance/player/${playerSummary.player.id}`)}
          >
            View Details
          </Button>
          {canWrite('finance') && playerSummary.netBalance > 0 && (
            <Button variant="ghost" size="sm" className="text-primary-600 hover:text-primary-700">
              Send Reminder
            </Button>
          )}
        </div>
      )
    },
  ];

  const guardianColumns = [
    { 
      key: 'guardian', 
      header: 'Guardian',
      render: (guardianSummary: GuardianFinancialSummary) => (
        <div>
          <div className="font-medium text-secondary-900">{guardianSummary.guardian.name}</div>
          <div className="text-sm text-secondary-600">{guardianSummary.guardian.email}</div>
          {guardianSummary.guardian.phone && (
            <div className="text-xs text-secondary-500">{guardianSummary.guardian.phone}</div>
          )}
        </div>
      )
    },
    { 
      key: 'linkedPlayers', 
      header: 'Linked Players',
      render: (guardianSummary: GuardianFinancialSummary) => {
        const displayLimit = 2;
        const hasMore = guardianSummary.linkedPlayers.length > displayLimit;
        const displayPlayers = guardianSummary.linkedPlayers.slice(0, displayLimit);
        
        return (
          <div className="space-y-1">
            {displayPlayers.map((playerSummary, index) => (
              <div key={index} className="text-sm">
                <span className="font-medium text-secondary-900">
                  {playerSummary.user.name}
                </span>
              </div>
            ))}
            {hasMore && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs text-primary-600 hover:text-primary-700 p-0"
                onClick={() => {
                  setSelectedGuardian(guardianSummary);
                  setShowDetailsModal(true);
                }}
              >
                +{guardianSummary.linkedPlayers.length - displayLimit} more players
              </Button>
            )}
          </div>
        );
      }
    },
    { 
      key: 'financialSummary', 
      header: 'Financial Summary',
      render: (guardianSummary: GuardianFinancialSummary) => (
        <div className={`font-medium text-lg ${guardianSummary.totalNetBalance > 0 ? 'text-success-600' : guardianSummary.totalNetBalance < 0 ? 'text-error-600' : 'text-secondary-900'}`}>
          Net Balance: {defaultCurrency} {guardianSummary.totalNetBalance.toFixed(2)}
        </div>
      )
    },
    { 
      key: 'actions', 
      header: 'Actions',
      render: (guardianSummary: GuardianFinancialSummary) => (
        <div className="flex items-center space-x-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate(`/finance/guardian/${guardianSummary.guardian.id}`)}
          >
            View Details
          </Button>
          {canWrite('finance') && guardianSummary.totalNetBalance > 0 && (
            <Button variant="ghost" size="sm" className="text-primary-600 hover:text-primary-700">
              Send Invoice
            </Button>
          )}
        </div>
      )
    },
  ];

  const playersTab = (
    <div className="space-y-6">
      {/* Summary Cards for Players - Organization-wide stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-sm text-secondary-600">Total Players</div>
          <div className="text-2xl font-bold text-secondary-900">
            {totalPlayers}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-secondary-600">Settled Invoices</div>
          <div className="text-2xl font-bold text-success-600">
            {loadingStats ? '...' : (orgStats?.settledCount ?? 0)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-secondary-600">Pending Invoices</div>
          <div className="text-2xl font-bold text-warning-600">
            {loadingStats ? '...' : (orgStats?.pendingCount ?? 0)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-secondary-600">Overdue Invoices</div>
          <div className="text-2xl font-bold text-error-600">
            {loadingStats ? '...' : (orgStats?.overdueCount ?? 0)}
          </div>
        </Card>
      </div>

      {/* Players Table */}
      <Card className="relative">
        {loading && (
          <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        )}
        <DataTable
          data={filteredPlayers}
          columns={playerColumns}
          emptyMessage="No players found"
          showPagination={false}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleSort}
        />

        {/* Pagination Controls */}
        {effectiveTotalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="text-sm text-secondary-600">
              Page {currentPage + 1} of {effectiveTotalPages} ({effectiveTotalCount} total players)
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0 || loading}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(effectiveTotalPages - 1, prev + 1))}
                disabled={currentPage >= effectiveTotalPages - 1 || loading}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );

  const guardiansTab = (
    <div className="space-y-6">
      {/* Summary Cards for Guardians */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="text-sm text-secondary-600">Total Guardians</div>
          <div className="text-2xl font-bold text-secondary-900">{totalGuardians}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-secondary-600">Total Players (on page)</div>
          <div className="text-2xl font-bold text-primary-600">
            {filteredGuardians.reduce((sum, g) => sum + g.linkedPlayers.length, 0)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-secondary-600">Guardians with Overdue</div>
          <div className="text-2xl font-bold text-error-600">
            {filteredGuardians.filter(g => g.totalNetBalance < 0).length}
          </div>
        </Card>
      </div>

      {/* Guardians Table */}
      <Card>
        {guardianLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <DataTable
            data={filteredGuardians}
            columns={guardianColumns}
            emptyMessage="No guardians found"
            showPagination={false}
            itemsPerPage={10}
          />
        )}
        {/* Algolia Pagination Controls for Guardians */}
        {guardianTotalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="text-sm text-secondary-600">
              Page {guardianCurrentPage + 1} of {guardianTotalPages} ({totalGuardians} total guardians)
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setGuardianCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={guardianCurrentPage === 0 || guardianLoading}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setGuardianCurrentPage(prev => Math.min(guardianTotalPages - 1, prev + 1))}
                disabled={guardianCurrentPage >= guardianTotalPages - 1 || guardianLoading}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );

  const tabs = [
    { label: 'Players', content: playersTab },
    { label: 'Guardians', content: guardiansTab }
  ];

  if (loading && !initialLoadDone) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Players & Guardians</h1>
            <p className="text-gray-600 mt-1">Financial overview and management</p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              loadFinancialData();
              loadOrganizationStats();
            }}
            disabled={loading || loadingStats}
          >
            Refresh Data
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex-1 w-full sm:max-w-md">
          <Input
            type="text"
            placeholder="Search players or guardians..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        <div className="flex items-center space-x-3">
          <Select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(0); }}
            className="w-40"
          >
            {statusOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-40"
          >
            {sortOptions.map(opt => (
              <option key={opt.value} value={opt.value}>Sort by {opt.label}</option>
            ))}
          </Select>
        </div>
      </div>

      {/* Tabs */}
      <Card>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
        />
      </Card>

      {/* Action Buttons */}
      {canWrite('finance') && (
        <div className="flex justify-end space-x-3">
          <Button variant="outline">
            {activeTab === 0 ? 'Send Bulk Reminders' : 'Send Bulk Invoices'}
          </Button>
          <Button variant="outline">
            Export Financial Report
          </Button>
        </div>
      )}

      {/* Guardian Financial Details Modal */}
      {showDetailsModal && selectedGuardian && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-full p-4">
            <div className="w-full max-w-6xl my-8">
              <Card className="w-full">
                <div className="p-6 border-b bg-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-bold text-secondary-900">Guardian Financial Details</h3>
                      <p className="text-sm text-secondary-600 mt-1">
                        Guardian: {selectedGuardian.guardian.name} • {selectedGuardian.guardian.email}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        setSelectedGuardian(null);
                      }}
                      className="text-secondary-400 hover:text-secondary-600"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  {/* Financial Summary */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-secondary-900 mb-4">Financial Overview</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Card className={`p-4 ${
                        (selectedGuardian.totalOutstanding || 0) > 0 
                          ? 'bg-gradient-to-br from-red-50 to-orange-100 border-red-200' 
                          : 'bg-gradient-to-br from-green-50 to-emerald-100 border-green-200'
                      }`}>
                        <div className="text-sm text-secondary-600">Outstanding Balance</div>
                        <div className={`text-2xl font-bold ${
                          (selectedGuardian.totalOutstanding || 0) > 0 
                            ? 'text-red-700' 
                            : 'text-green-700'
                        }`}>
                          {defaultCurrency} {(selectedGuardian.totalOutstanding || 0).toFixed(2)}
                        </div>
                      </Card>
                      
                      <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200">
                        <div className="text-sm text-secondary-600">Available Credits</div>
                        <div className="text-2xl font-bold text-blue-700">
                          {defaultCurrency} {(selectedGuardian.totalCredits || 0).toFixed(2)}
                        </div>
                      </Card>
                      
                      <Card className={`p-4 ${
                        (selectedGuardian.totalNetBalance || 0) > 0 
                          ? 'bg-gradient-to-br from-red-50 to-orange-100 border-red-200' 
                          : 'bg-gradient-to-br from-green-50 to-emerald-100 border-green-200'
                      }`}>
                        <div className="text-sm text-secondary-600">Net Amount Due</div>
                        <div className={`text-2xl font-bold ${
                          (selectedGuardian.totalNetBalance || 0) > 0 
                            ? 'text-red-700' 
                            : 'text-green-700'
                        }`}>
                          {defaultCurrency} {(selectedGuardian.totalNetBalance || 0).toFixed(2)}
                        </div>
                      </Card>
                    </div>
                  </div>

                  {/* Guardian's Linked Players */}
                  {selectedGuardian && (
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-secondary-900 mb-4">Linked Players</h4>
                      <div className="space-y-2">
                        {selectedGuardian.linkedPlayers.map((playerSummary, index) => {
                          const status = getFinancialStatus(playerSummary);
                          return (
                            <div key={index} className="flex justify-between items-center p-3 bg-secondary-50 rounded-lg">
                              <div>
                                <div className="font-medium text-secondary-900">
                                  {playerSummary.user.name}
                                </div>
                                <div className="text-sm text-secondary-600">
                                  Outstanding: {defaultCurrency} {playerSummary.outstandingDebits.toFixed(2)}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className={`font-medium ${
                                  playerSummary.netBalance > 0 ? 'text-error-600' : 
                                  playerSummary.netBalance < 0 ? 'text-success-600' : 'text-secondary-900'
                                }`}>
                                  {defaultCurrency} {playerSummary.netBalance.toFixed(2)}
                                </div>
                                <Badge variant={getStatusColor(status)} className="mt-1">
                                  {getStatusDisplayLabel(status)}
                                </Badge>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}


                  {/* Footer */}
                  <div className="flex justify-end space-x-3">
                    <Button variant="outline" size="sm">
                      Export Report
                    </Button>
                    <Button
                      onClick={() => {
                        setShowDetailsModal(false);
                        setSelectedGuardian(null);
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default PlayersGuardians;